import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getSpecialNpcEmoji } from '@danda/shared/utils/avatar-emoji';
import { getStaffAvatarUrl, getVideoUrl } from '@/lib/media';
import { ImageIcon, ChevronDown, ChevronUp, Swords, Heart, LayoutGrid, List } from 'lucide-react';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';
import ManagedVideo from '@/components/common/ManagedVideo';
import ImageModal from '@/components/common/ImageModal';
import SceneCharsSection from '@/components/panels/SceneCharsSection';
import SceneItemsSection from '@/components/panels/SceneItemsSection';
import { PresentEntry } from '@/components/panels/PresentEntry';
import type { PresentEntryData } from '@/components/panels/PresentEntry';
import { usePresentViewMode } from '@/lib/hooks/usePresentViewMode';
import { useLobbyStore, useSelectedCharacter } from '@/lib/stores/lobbyStore';
import { useGameStore, deriveHealthState } from '@/lib/stores/gameStore';
import { isHiddenSceneCorpse } from './scene-chars-helpers';
import { websocketService } from '@/lib/websocket-service';
import { track } from '@/lib/analytics';
import { toast } from '@/lib/toast';
import { usePmBalanceStore } from '@/lib/stores/pmBalanceStore';
import type { LobbyCharacterInfo } from '@danda/shared';
import { CharacterTypeEnum, RoomTypeEnum, MIN_PM_BALANCE } from '@danda/shared';
import type { HealthState } from '@danda/shared';
import { usePanelCollapse } from '@/lib/hooks/usePanelCollapse';
import { formatSceneName } from '@/lib/utils';
import { HpBar } from '@/components/ui/hp-bar';
import { cn } from '@/lib/utils';

/** Color config for observable health states — bar fill + glow */
const healthStateColors: Record<HealthState, { bar: string; glow: string; label: string }> = {
  uninjured: { bar: 'bg-green-500', glow: 'shadow-green-500/30', label: 'UNINJURED' },
  wounded: { bar: 'bg-yellow-500', glow: 'shadow-yellow-500/30', label: 'WOUNDED' },
  bloodied: { bar: 'bg-orange-500', glow: 'shadow-orange-500/30', label: 'BLOODIED' },
  critical: { bar: 'bg-red-500', glow: 'shadow-red-500/30', label: 'CRITICAL' },
  dying: { bar: 'bg-red-800 animate-pulse', glow: 'shadow-red-800/40', label: 'DYING' },
  dead: { bar: 'bg-gray-600', glow: 'shadow-gray-600/20', label: 'DOWN' },
};

/** Approximate width percentage for observable health states (mid-range of each tier) */
const healthStateWidth: Record<HealthState, number> = {
  uninjured: 100,
  wounded: 75,
  bloodied: 35,
  critical: 12,
  dying: 5,
  dead: 0,
};

/** Hardcoded lobby staff NPCs — always present, chat-clickable. */
const STAFF_NPCS = [
  {
    id: 'tavian',
    name: 'Tavian',
    file: 'tavian-avatar.webp',
    emoji: '🧙‍♂️',
    desc: 'The Portal Keeper',
    tooltip: 'Level 20 Elf Wizard: The Portal Keeper',
    aria: 'Chat with Tavian, the Portal Keeper',
  },
  {
    id: 'aldric',
    name: 'Aldric',
    file: 'aldric-avatar.webp',
    emoji: '🌟',
    desc: 'Keeper of Sacred Rites',
    tooltip: 'Level 15 Human Cleric: Keeper of Sacred Rites',
    aria: 'Chat with Brother Aldric for character creation',
  },
  {
    id: 'kira',
    name: 'Kira',
    file: 'kira-avatar.webp',
    emoji: '⚔️',
    desc: 'Blademaster of the Arena',
    tooltip: 'Level 18 Half-Elf Fighter: Blademaster of the Arena',
    aria: 'Chat with Master Kira for leveling up',
  },
  {
    id: 'grolt',
    name: 'Grolt',
    file: 'grolt-avatar.webp',
    emoji: '⚒️',
    desc: 'Master of Wares',
    tooltip: 'Level 12 Half-Orc Artificer: Master of Wares',
    aria: 'Chat with Grolt the merchant',
  },
] as const satisfies ReadonlyArray<{
  id: 'tavian' | 'aldric' | 'kira' | 'grolt';
  name: string;
  file: string;
  emoji: string;
  desc: string;
  tooltip: string;
  aria: string;
}>;

/** Observable health bar — color-coded, no exact numbers */
function ObservableHpBar({ healthState }: { healthState: HealthState }) {
  const config = healthStateColors[healthState];
  const width = healthStateWidth[healthState];

  return (
    <div className="space-y-0.5">
      <span
        className={cn('text-[10px] font-semibold uppercase tracking-wider', {
          'text-green-400': healthState === 'uninjured',
          'text-yellow-400': healthState === 'wounded',
          'text-orange-400': healthState === 'bloodied',
          'text-red-400': healthState === 'critical',
          'text-red-700': healthState === 'dying',
          'text-gray-500': healthState === 'dead',
        })}
      >
        {config.label}
      </span>
      <div className={cn('h-2 bg-muted rounded-full overflow-hidden shadow-sm', config.glow)}>
        <div
          className={cn('h-full rounded-full transition-all duration-500', config.bar)}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

interface ScenePanelProps {
  // Context is now derived from store, but prop can override
  context?: 'lobby' | 'character-creation' | 'party-ready' | 'game-session';
  className?: string;
}

export default function ScenePanel({ context: propContext, className }: ScenePanelProps) {
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [isExpanded, toggleExpanded] = usePanelCollapse('scene');
  const [sceneOverlayOpen, setSceneOverlayOpen] = useState(false);

  // Lobby Store — useShallow so re-renders track only the selected fields.
  const { switchChatRoom, lobbyCharacters, parties } = useLobbyStore(
    useShallow((s) => ({
      switchChatRoom: s.switchChatRoom,
      lobbyCharacters: s.lobbyCharacters,
      parties: s.parties,
    }))
  );
  const selectedCharacter = useSelectedCharacter();
  // Subscribe only to the active room's `type` (a primitive). The previous
  // useActiveChatRoom() returned the whole ChatRoom object, which `addMessage`
  // replaces on every active-room message — re-rendering ScenePanel for chat
  // traffic it doesn't need.
  const activeChatRoomType = useLobbyStore(
    (s) => s.chatRooms.find((r) => r.id === s.activeChatRoomId)?.type ?? null
  );

  const [presentViewMode, setPresentViewMode] = usePresentViewMode();

  // Get all character IDs that are in any party (to filter from lobby "Present" list)
  const partyMemberIds = useMemo(
    () => new Set(parties.flatMap((p) => p.memberCharacterIds)),
    [parties]
  );

  // Separate lobby characters by type for display
  // Note: special NPCs (Tavian, Aldric, Kira) are hardcoded in UI since they're always present
  // Filter out NPCs that are already in a party - they shouldn't appear in the lobby
  const rosterNpcs = useMemo(
    () =>
      lobbyCharacters.filter((c) => c.type === CharacterTypeEnum.NPC && !partyMemberIds.has(c.id)),
    [lobbyCharacters, partyMemberIds]
  );
  const playerCharacters = useMemo(
    () =>
      lobbyCharacters.filter((c) => c.type === CharacterTypeEnum.PC && !partyMemberIds.has(c.id)),
    [lobbyCharacters, partyMemberIds]
  );

  // Debug logging - only log when lobbyCharacters changes, not on every render
  useEffect(() => {
    if (lobbyCharacters.length > 0) {
      console.log('[ScenePanel] Lobby characters updated', {
        total: lobbyCharacters.length,
        npcs: lobbyCharacters.filter((c) => c.type === CharacterTypeEnum.NPC).length,
        pcs: lobbyCharacters.filter((c) => c.type === CharacterTypeEnum.PC).length,
      });
    }
  }, [lobbyCharacters]);

  // Game Store — useShallow so re-renders track only the selected fields.
  const {
    currentScene,
    isSceneLoading,
    connectionStatus,
    partyMembers,
    sceneCharacters,
    playerCharacterId,
    sceneItems,
    sceneInteractables,
  } = useGameStore(
    useShallow((s) => ({
      currentScene: s.currentScene,
      isSceneLoading: s.isSceneLoading,
      connectionStatus: s.connectionStatus,
      partyMembers: s.partyMembers,
      sceneCharacters: s.sceneCharacters,
      playerCharacterId: s.playerCharacterId,
      sceneItems: s.sceneItems,
      sceneInteractables: s.sceneInteractables,
    }))
  );
  const isGameConnected = connectionStatus.status === 'connected';
  const { pathname } = useLocation();
  const isTrainingSession = pathname.includes('party_training_');
  // Use playerCharacterId to find the correct player member (not just [0])
  const playerMember = partyMembers.find((m) => m.charId === playerCharacterId) ?? partyMembers[0];

  // Derive context
  const isGameConnecting =
    connectionStatus.status === 'validating' || connectionStatus.status === 'connecting';
  const derivedContext =
    propContext ||
    (() => {
      if (isGameConnected && currentScene) return 'game-session';
      if (isGameConnecting) return 'game-session'; // Don't flash lobby while connecting
      if (activeChatRoomType === RoomTypeEnum.ALDRIC) return 'character-creation';
      if (activeChatRoomType === RoomTypeEnum.GROLT) return 'merchant';
      if (activeChatRoomType === RoomTypeEnum.PARTY) return 'party-ready';
      return 'lobby'; // Default (Tavian 1:1)
    })();

  // Stable boolean: are there living hostile creatures in the scene?
  const hasLivingHostiles = sceneCharacters.some(
    (c) => c.healthState !== 'dead' && (c.type === 'monster' || c.attitude === 'hostile')
  );

  // Persistent ref to the <img> DOM node so the effect can re-check cache status
  const imgNodeRef = useRef<HTMLImageElement | null>(null);
  // Ref to the video DOM node so the effect can re-check readyState for cached videos
  const videoNodeRef = useRef<HTMLVideoElement | null>(null);

  // Reset image/video loaded state when scene changes
  useEffect(() => {
    setMediaLoaded(false);
    setMediaError(false);
    // Re-check: if image already loaded from cache, the ref callback fired
    // before this effect (React commit phase < effect phase), so re-verify.
    // Both setState calls are batched — React sees the final value.
    // For images: re-check if already loaded from cache. Guard with !videoNodeRef.current
    // so a stale image ref doesn't short-circuit the video readyState check when the
    // scene switches from an image scene to a video scene. Use the ref rather than
    // isVideo, which is declared further down the component.
    if (!videoNodeRef.current) {
      const node = imgNodeRef.current;
      if (node?.complete && node.naturalWidth > 0) {
        setMediaLoaded(true);
        return;
      }
    }
    // For video: canplay may have fired during the render that changed the scene,
    // but this effect runs after and resets mediaLoaded back to false. Re-check
    // readyState here so cached/already-buffered videos don't stay invisible.
    const video = videoNodeRef.current;
    if (video && video.readyState >= 2) {
      setMediaLoaded(true);
    }
  }, [
    currentScene?.imageUrl,
    currentScene?.videoUrl,
    currentScene?.encounterImageUrl,
    currentScene?.encounterVideoUrl,
    hasLivingHostiles,
    derivedContext,
  ]);

  // Handle cached images that complete before React attaches onLoad
  const imgRef = useCallback((node: HTMLImageElement | null) => {
    imgNodeRef.current = node;
    if (node?.complete && node.naturalWidth > 0) {
      setMediaLoaded(true);
    }
  }, []);

  const getSceneData = () => {
    if (derivedContext === 'game-session' && currentScene) {
      const showEncounter = hasLivingHostiles && !!currentScene.encounterVideoUrl;
      return {
        title: formatSceneName(currentScene.name),
        description: currentScene.description,
        imageUrl:
          hasLivingHostiles && !currentScene.encounterVideoUrl
            ? currentScene.encounterImageUrl || currentScene.imageUrl || null
            : hasLivingHostiles
              ? null
              : currentScene.imageUrl || null,
        videoUrl: showEncounter ? currentScene.encounterVideoUrl : currentScene.videoUrl || null,
      };
    }

    // Static scenes for other contexts
    const staticScenes = {
      lobby: {
        title: 'The Hilltop Camp',
        description:
          'An open-air campsite atop a hill under a full moon, where a shimmering portal welcomes travelers.',
        imageUrl: null,
        videoUrl: getVideoUrl('lobby_default.mp4'),
      },
      merchant: {
        title: "Grolt's Wares",
        description:
          'A battered cart near the portal, stocked with the finest steel and sharpest blades.',
        imageUrl: null,
        videoUrl: getVideoUrl('grolt.mp4'),
      },
      'character-creation': {
        title: 'Temple of Tymora',
        description: 'Sacred grounds where Brother Aldric conducts character creation rites',
        imageUrl: null,
        videoUrl: null,
      },
      'party-ready': {
        title: 'Adventure Awaits',
        description: 'Your party is ready for the next quest',
        imageUrl: null,
        videoUrl: null,
      },
    };

    return staticScenes[derivedContext as keyof typeof staticScenes] || staticScenes.lobby;
  };

  const sceneData = getSceneData();
  const isLoading = derivedContext === 'game-session' ? isSceneLoading : false;

  const isVideo = !!sceneData.videoUrl;
  const mediaUrl = sceneData.videoUrl || sceneData.imageUrl;
  const handleMediaLoaded = () => setMediaLoaded(true);
  const handleMediaError = () => {
    setMediaLoaded(false);
    setMediaError(true);
  };

  // SVG placeholder for scene image (16:9 aspect ratio)
  const PlaceholderImage = () => (
    <svg
      viewBox="0 0 320 180"
      className="w-full h-auto rounded-md"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="320" height="180" className="fill-muted" rx="4" />
      <path
        d="M40 145 L80 100 L120 120 L160 70 L200 105 L240 75 L280 115 L280 145 Z"
        className="fill-muted-foreground/20"
      />
      <circle cx="260" cy="50" r="20" className="fill-muted-foreground/30" />
      <path
        d="M60 145 L95 115 L130 130 L160 100 L195 120 L230 90 L265 130 L265 145 Z"
        className="fill-muted-foreground/25"
      />
      <text
        x="160"
        y="165"
        textAnchor="middle"
        className="fill-muted-foreground"
        fontSize="12"
        fontFamily="sans-serif"
      >
        Scene Image
      </text>
    </svg>
  );

  // Handle clicking on special NPCs (Tavian, Aldric, Kira, Grolt) - switches main chat pane
  const handleSpecialNPCClick = (npcId: 'tavian' | 'aldric' | 'kira' | 'grolt') => {
    const lobbyState = useLobbyStore.getState();

    // Already chatting with this NPC — just focus the tab, don't restart the session
    if (lobbyState.mainChatNpc === npcId) {
      lobbyState.switchMainNpc(npcId);
      return;
    }

    lobbyState.switchMainNpc(npcId);
    track('npc_chat_open', { npc_id: npcId, context: 'lobby' });

    // When clicking Aldric directly, also start the character creation session
    // This ensures char-create-start is sent before the user types a message
    if (npcId === 'aldric') {
      lobbyState.setWaitingForResponse(true);
      track('character_create_start', { path: 'weave' });
      websocketService.startCharacterCreationSession();
    }

    // When clicking Grolt directly, start the merchant session
    if (npcId === 'grolt') {
      const charId = lobbyState.selectedCharacterId;
      if (charId) {
        lobbyState.setWaitingForResponse(true);
        websocketService.startMerchantSession(charId);
      }
    }

    // When clicking Kira directly, start the level-up session
    if (npcId === 'kira') {
      const charId = lobbyState.selectedCharacterId;
      if (charId) {
        lobbyState.setWaitingForResponse(true);
        websocketService.startLevelUp(charId);
      }
    }
  };

  // Handle clicking on roster NPCs - starts a character chat session
  // Only characters can chat with NPCs - players must create a character first
  const handleRosterNPCClick = (npc: LobbyCharacterInfo) => {
    // Require a character to chat with NPCs. Silently ignoring the click reads
    // as a broken button, so say why nothing happened.
    if (!selectedCharacter) {
      toast.info(
        'You need to have a character in order to interact with other characters. Ask Aldric to help you create one.',
        { duration: 5000 }
      );
      return;
    }

    track('npc_chat_open', { npc_id: npc.id, context: 'lobby' });

    // Check if chat session already exists for this NPC by character ID (not name - chars may be renamed)
    const existingRoom = useLobbyStore
      .getState()
      .chatRooms.find(
        (room) => room.id.startsWith('char-chat-') && room.participants.includes(npc.id)
      );

    if (existingRoom) {
      // Switch to existing chat room
      switchChatRoom(existingRoom.id);
    } else {
      // Start a new character chat - server will create session and send char-chat-started
      // The lobbyStore handler will create the room when it receives the event.
      // Opening a fresh chat round-trips to the server, so give instant feedback,
      // but skip the toast when the send will be dropped by the low-PM-balance gate
      // (the command layer renders a token gate instead of opening the chat).
      const balance = usePmBalanceStore.getState().pmBalance;
      const gatedByBalance = balance !== null && balance < MIN_PM_BALANCE;
      if (!gatedByBalance) {
        toast.info(`Approaching ${npc.name}...`, { duration: 2000 });
      }
      websocketService.startCharacterChat(npc.id, npc.name);
    }
  };

  // Non-staff lobby characters (roster NPCs, other players, and the user's own
  // character), merged and sorted alphabetically by name. Rendered after the
  // fixed staff NPCs in both icon and list views.
  const sortedPresentEntries: (PresentEntryData & { key: string })[] = [
    ...rosterNpcs.map((npc) => ({
      key: npc.id,
      name: npc.name,
      avatarSrc: npc.avatarUrl || npc.avatar,
      avatarFallback: npc.avatarEmoji || npc.name.charAt(0),
      tooltip: `Level ${npc.level} ${npc.species} ${npc.class}`,
      description: `Level ${npc.level} ${npc.species} ${npc.class}`,
      ariaLabel: `Chat with ${npc.name}, ${npc.class} level ${npc.level}`,
      onClick: () => handleRosterNPCClick(npc),
    })),
    ...playerCharacters
      .filter((pc) => pc.id !== selectedCharacter?.id)
      .map((pc) => ({
        key: pc.id,
        name: pc.name,
        avatarSrc: pc.avatarUrl || pc.avatar,
        avatarFallback: pc.avatarEmoji || pc.name.charAt(0),
        tooltip: `Level ${pc.level} ${pc.species} ${pc.class}`,
        description: `Level ${pc.level} ${pc.species} ${pc.class}`,
        ariaLabel: `Chat with ${pc.name}, ${pc.class} level ${pc.level}`,
        onClick: () => handleRosterNPCClick(pc),
      })),
    ...(selectedCharacter && !partyMemberIds.has(selectedCharacter.id)
      ? [
          {
            key: selectedCharacter.id,
            name: selectedCharacter.name,
            avatarSrc:
              selectedCharacter.avatarUrl ||
              selectedCharacter.avatar ||
              (selectedCharacter.permanent?.avatar_url as string | undefined) ||
              (selectedCharacter.permanent?.avatarUrl as string | undefined),
            avatarFallback:
              selectedCharacter.avatarEmoji ||
              selectedCharacter.permanent?.avatar_emoji ||
              (selectedCharacter.name || '')
                .split(' ')
                .map((n) => n[0])
                .join(''),
            tooltip: `Level ${selectedCharacter.permanent?.level ?? '?'} ${selectedCharacter.permanent?.species ?? ''} ${selectedCharacter.permanent?.class ?? ''} (click to chat)`,
            description: `Level ${selectedCharacter.permanent?.level ?? '?'} ${selectedCharacter.permanent?.species ?? ''} ${selectedCharacter.permanent?.class ?? ''}`,
            ariaLabel: `Open inner voice chat with ${selectedCharacter.name}`,
            onClick: () => switchChatRoom(`inner-${selectedCharacter.id}`),
            selfRing: true,
          },
        ]
      : []),
  ].sort((a, b) => a.name.localeCompare(b.name));

  return (
    // `flex flex-col` + `grow`/`min-h-0` on the inner chain lets the Present
    // list absorb leftover height when a parent constrains this card (lobby
    // left column). With flex-basis left at `auto`, an unconstrained parent
    // (game view) still sizes the card to its content, unchanged.
    // Collapsed, the card is header-only, so cancel any `grow` the caller
    // passed: otherwise it stretches into a tall empty card.
    // overflow-hidden makes the card a real box: when a short viewport squeezes
    // it, CardContent scrolls inside instead of painting the roster out through
    // the bottom edge and over the panel below. min-h-14 is the floor that
    // keeps the header (and its collapse button) on screen, since overflow-hidden
    // would otherwise clip them once flex shrinking takes the card below it.
    <Card
      className={cn(
        'flex flex-col overflow-hidden',
        className,
        'min-h-14',
        !isExpanded && 'grow-0'
      )}
    >
      <CardHeader className="shrink-0 pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {isTrainingSession && derivedContext === 'game-session' ? (
              <>
                <Swords className="h-4 w-4" />
                Duel
              </>
            ) : (
              <>
                <ImageIcon className="h-4 w-4" />
                {sceneData?.title || 'Scene'}
              </>
            )}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={toggleExpanded}
            aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="flex min-h-0 grow flex-col overflow-y-auto p-3 pt-1 space-y-3">
          {/* Scene Image — split portrait view during training duels */}
          <div className="shrink-0 overflow-hidden rounded-md">
            {isTrainingSession && derivedContext === 'game-session' && playerMember ? (
              <>
                <div className="relative aspect-video bg-gradient-to-b from-zinc-900 to-zinc-950 flex">
                  {/* Player portrait — left half */}
                  <div className="flex-1 relative overflow-hidden">
                    {playerMember?.portraitUrl || playerMember?.avatarUrl ? (
                      <img
                        src={playerMember.portraitUrl || playerMember.avatarUrl}
                        alt={playerMember?.name ?? 'Player'}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-green-950/30">
                        <span className="text-3xl">{playerMember?.avatarEmoji || '⚔️'}</span>
                      </div>
                    )}
                    {/* Dark gradient overlay from center */}
                    <div className="absolute inset-0 bg-gradient-to-l from-black/70 via-transparent to-transparent" />
                    {/* Name label */}
                    <div className="absolute bottom-2 left-2">
                      <span className="text-xs font-bold text-white/90 drop-shadow-lg font-character">
                        {playerMember?.name ?? 'Player'}
                      </span>
                    </div>
                  </div>

                  {/* VS divider — centered between portraits */}
                  <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-px h-6 bg-gradient-to-b from-transparent to-amber-500/60" />
                      <Swords className="h-5 w-5 text-amber-500/80 drop-shadow-lg" />
                      <div className="w-px h-6 bg-gradient-to-t from-transparent to-amber-500/60" />
                    </div>
                  </div>

                  {/* Opponent portrait — right half */}
                  <div className="flex-1 relative overflow-hidden">
                    {sceneCharacters[0]?.portraitUrl || sceneCharacters[0]?.avatarUrl ? (
                      <img
                        src={sceneCharacters[0].portraitUrl || sceneCharacters[0].avatarUrl}
                        alt={sceneCharacters[0]?.name ?? 'Opponent'}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-red-950/30">
                        <span className="text-3xl">{sceneCharacters[0]?.avatarEmoji || '👹'}</span>
                      </div>
                    )}
                    {/* Dark gradient overlay from center */}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />
                    {/* Name label */}
                    <div className="absolute bottom-2 right-2">
                      <span className="text-xs font-bold text-white/90 drop-shadow-lg font-character">
                        {sceneCharacters[0]?.name ?? 'Opponent'}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Health tracking below portraits */}
                <div className="mt-2 flex items-stretch gap-3">
                  {/* Player HP — exact */}
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-1">
                      <Heart className="h-3 w-3 text-red-500" />
                      <span className="text-xs font-medium">
                        {playerMember?.hp}/{Math.max(1, playerMember?.maxHp ?? 1)}
                      </span>
                    </div>
                    <HpBar
                      current={playerMember?.hp ?? 0}
                      max={Math.max(1, playerMember?.maxHp ?? 1)}
                    />
                  </div>

                  {/* VS spacer */}
                  <div className="flex items-center px-1">
                    <span className="text-[10px] font-bold text-amber-500/60">VS</span>
                  </div>

                  {/* Opponent HP — observable */}
                  <div className="flex-1 min-w-0">
                    {sceneCharacters[0] ? (
                      <ObservableHpBar
                        healthState={sceneCharacters[0]?.healthState ?? 'uninjured'}
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground italic">Awaiting...</span>
                    )}
                  </div>
                </div>
              </>
            ) : isLoading ? (
              <div className="aspect-video bg-muted flex items-center justify-center">
                <div className="flex flex-col items-center gap-2">
                  <AnkhSpinner size="lg" className="text-accent-gold" />
                  <span className="text-xs text-muted-foreground">Generating scene...</span>
                </div>
              </div>
            ) : mediaError ? (
              <PlaceholderImage />
            ) : mediaUrl ? (
              isVideo ? (
                <ManagedVideo
                  ref={videoNodeRef}
                  src={mediaUrl}
                  paused={sceneOverlayOpen}
                  className={`w-full aspect-video object-cover rounded-md transition-opacity duration-300 cursor-pointer ${
                    mediaLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  onCanPlay={handleMediaLoaded}
                  onError={handleMediaError}
                  onClick={() => mediaLoaded && setSceneOverlayOpen(true)}
                />
              ) : (
                <img
                  key={mediaUrl}
                  ref={imgRef}
                  src={mediaUrl}
                  alt={sceneData.title}
                  className={`w-full aspect-video object-cover transition-opacity duration-300 cursor-pointer ${
                    mediaLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={handleMediaLoaded}
                  onError={handleMediaError}
                  onClick={() => mediaLoaded && setSceneOverlayOpen(true)}
                />
              )
            ) : (
              <PlaceholderImage />
            )}
          </div>
          <ImageModal
            isOpen={sceneOverlayOpen}
            onClose={() => setSceneOverlayOpen(false)}
            imageUrl={isVideo ? undefined : (mediaUrl ?? undefined)}
            videoUrl={isVideo ? (mediaUrl ?? undefined) : undefined}
            altText={sceneData.title}
          />

          {/* Scene Info */}
          {!isLoading && sceneData && (
            <div className="flex min-h-0 grow flex-col space-y-2">
              <p className="shrink-0 text-sm text-foreground/70 select-none">
                {sceneData.description}
              </p>

              {/* Game-session sections: chars + items, with rule-based separators */}
              {derivedContext === 'game-session' &&
                (() => {
                  // #248: count only the chars SceneCharsSection will actually
                  // render (corpses are hidden) — otherwise a corpses-only scene
                  // shows the section's gold dividers around an empty panel.
                  const charsVisible = sceneCharacters.some((c) => {
                    const healthState =
                      c.healthState ??
                      deriveHealthState(c.hp ?? c.maxHp ?? 10, c.maxHp ?? 10, c.conditions);
                    return !isHiddenSceneCorpse(healthState, c.type);
                  });
                  const itemsVisible = sceneItems.length > 0 || sceneInteractables.length > 0;
                  const goldDivider = (
                    <div className="h-px w-full bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />
                  );
                  return (
                    <>
                      {(charsVisible || itemsVisible) && goldDivider}
                      {charsVisible && <SceneCharsSection />}
                      {charsVisible && itemsVisible && goldDivider}
                      {itemsVisible && <SceneItemsSection />}
                      {(charsVisible || itemsVisible) && goldDivider}
                    </>
                  );
                })()}

              {/* Characters in scene - Only show in lobby for now */}
              {derivedContext === 'lobby' && (
                <div className="flex min-h-0 grow flex-col space-y-1">
                  <div className="flex shrink-0 items-center justify-between">
                    <h4 className="text-xs font-medium">Present</h4>
                    <div className="flex items-center gap-1">
                      <div className="flex items-center rounded-md border border-border overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setPresentViewMode('icons')}
                          className={cn(
                            'p-1 cursor-pointer transition-colors',
                            presentViewMode === 'icons'
                              ? 'bg-muted text-foreground'
                              : 'text-muted-foreground hover:bg-muted/50'
                          )}
                          title="Icon view"
                          aria-label="Icon view"
                          aria-pressed={presentViewMode === 'icons'}
                        >
                          <LayoutGrid className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPresentViewMode('list')}
                          className={cn(
                            'p-1 cursor-pointer transition-colors',
                            presentViewMode === 'list'
                              ? 'bg-muted text-foreground'
                              : 'text-muted-foreground hover:bg-muted/50'
                          )}
                          title="List view"
                          aria-label="List view"
                          aria-pressed={presentViewMode === 'list'}
                        >
                          <List className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div
                    className={cn(
                      // min-h-24 is a floor, not a height: the roster still
                      // grows into spare space, but a short viewport can no
                      // longer shrink it to nothing and hide Aldric, the
                      // character-creation entry point.
                      'min-h-24 grow overflow-y-auto pt-1',
                      presentViewMode === 'list'
                        ? 'flex flex-col gap-0.5'
                        : 'flex items-start gap-1 flex-wrap'
                    )}
                  >
                    {/* Special NPCs - always present in the lobby (clickable to chat) */}
                    {/* Staff NPCs first (fixed order), then all other lobby
                        characters merged and sorted alphabetically by name. */}
                    {STAFF_NPCS.map((npc) => (
                      <PresentEntry
                        key={npc.id}
                        viewMode={presentViewMode}
                        bold
                        name={npc.name}
                        avatarSrc={getStaffAvatarUrl(npc.file)}
                        avatarFallback={getSpecialNpcEmoji(npc.id) ?? npc.emoji}
                        tooltip={npc.tooltip}
                        description={npc.desc}
                        ariaLabel={npc.aria}
                        onClick={() => handleSpecialNPCClick(npc.id)}
                      />
                    ))}

                    {sortedPresentEntries.map(({ key, ...entry }) => (
                      <PresentEntry key={key} viewMode={presentViewMode} {...entry} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
